import { Option } from '../components/select/select.types';
import { abbreviations } from './data/timezoneAbbreviations';

export interface TimezoneBaseOption {
  label: string;
  value: string;
  alias: string;
  offset: string;
  filterLabel?: string;
}

export type TimezoneOption = Option<TimezoneBaseOption>;

let timezonesOptions: TimezoneOption[] | null = null;

function getTimezones() {
  return Intl.supportedValuesOf('timeZone');
}

function getTimezoneFormat(
  timezone: string | undefined,
  format: Intl.DateTimeFormatOptions['timeZoneName'],
  time: number = Date.now()
): string {
  return (
    new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: format })
      .formatToParts(new Date(time))
      .find((part) => part.type === 'timeZoneName')?.value ?? ''
  );
}

export function getTimezone(timezone: string): string {
  if (timezone === 'Local') {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  return timezone;
}

export function getTimezoneShortName(timezoneName?: string, time: number = Date.now()): string {
  const timezone = getTimezone(timezoneName || 'Local');
  const longName = getTimezoneFormat(timezone, 'long', time);
  return abbreviations[longName] || getTimezoneFormat(timezone, 'short', time);
}

function getTimezoneInfo(timezone?: string) {
  const shortName = getTimezoneShortName(timezone);

  const alias = shortName.startsWith('GMT+') || shortName.startsWith('GMT-') ? '' : shortName;
  const offset = getTimezoneFormat(timezone, 'longOffset').replace('GMT', 'UTC');

  return { alias, offset };
}

export function getTimezoneOptions() {
  if (!timezonesOptions) {
    timezonesOptions = getTimezones().map((timezone) => {
      const { alias, offset } = getTimezoneInfo(timezone);
      const filterLabel = `${timezone} - ${alias} - ${offset}`;
      return { label: timezone, value: timezone, alias, offset, filterLabel };
    });

    timezonesOptions = [
      { label: 'Coordinated Universal Time', value: 'UTC', alias: 'UTC', offset: 'UTC+00:00' },
      { label: 'Browser Time', value: 'Local', ...getTimezoneInfo() },
      { separator: true, key: 'defaultOptionsSeparator' },
      ...timezonesOptions
    ];
  }

  return timezonesOptions;
}

export function formatDate(date: Date, timezone: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
    .formatToParts(date)
    .filter((part) => part.type !== 'literal')
    .reduce((acc, part) => Object.assign(acc, { [part.type]: part.value }), { year: '', month: '', day: '' });
  return `${parts.year}-${parts.month}-${parts.day}`;
}

export function formatDateTime(date: Date, timezone: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
    .formatToParts(date)
    .filter((part) => part.type !== 'literal')
    .reduce((acc, part) => Object.assign(acc, { [part.type]: part.value }), {
      year: '',
      month: '',
      day: '',
      hour: '',
      minute: ''
    });
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
}
